import { type ReactElement } from 'react';
import { RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { SystemVersionDto } from '@/lib/adminApiClient';
import { formatVersionTime } from './formatVersionTime';

/**
 * VersionInfoDialog — modal popup displayed by clicking 版本信息 in the
 * sidebar (Issue #339).
 *
 * Display contract:
 *   - Always shows the four allowlisted manifest fields (release tag, last
 *     update time, short git SHA, metadata source).
 *   - Loading and failure states are explicit; no fake values are filled in
 *     while the network request is pending or after it errored.
 *   - Source `fallback` and `unavailable` get a small visual cue so admins
 *     don't mistake a dev-bootstrap version for production metadata.
 *   - Times are formatted via `formatVersionTime`, never rendered as raw
 *     `Date` objects.
 */

export interface VersionInfoDialogProps {
  readonly data: SystemVersionDto | null;
  readonly loading: boolean;
  readonly errorMessage: string | null;
  readonly onClose: () => void;
  readonly onRefresh: () => void;
}

const SOURCE_LABELS: Readonly<Record<SystemVersionDto['source'], string>> = {
  manifest: '部署 manifest',
  fallback: '开发兜底（非生产元数据）',
  unavailable: '暂无版本元数据',
};

export default function VersionInfoDialog({
  data,
  loading,
  errorMessage,
  onClose,
  onRefresh,
}: VersionInfoDialogProps): ReactElement {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="version-info-dialog-title"
      data-testid="version-info-dialog"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-background p-6 shadow-lg"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 id="version-info-dialog-title" className="text-base font-semibold">
            版本信息
          </h2>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1"
            onClick={onRefresh}
            disabled={loading}
            data-testid="version-info-dialog-refresh"
          >
            <RefreshCw className={loading ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} />
            {loading ? '加载中…' : '刷新'}
          </Button>
        </div>

        {loading && !data ? (
          <p
            className="text-sm text-muted-foreground"
            data-testid="version-info-dialog-loading"
          >
            正在读取部署版本信息…
          </p>
        ) : null}

        {!loading && errorMessage && !data ? (
          <div
            className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
            data-testid="version-info-dialog-error"
          >
            {errorMessage}
          </div>
        ) : null}

        {data ? (
          <dl
            className="grid grid-cols-[6.5rem_1fr] gap-y-2 text-sm"
            data-testid="version-info-dialog-content"
          >
            <dt className="text-muted-foreground">版本号</dt>
            <dd
              className="break-all font-medium"
              data-testid="version-info-dialog-release-tag"
            >
              {data.releaseTag ?? '未知'}
            </dd>
            <dt className="text-muted-foreground">更新时间</dt>
            <dd data-testid="version-info-dialog-built-at">
              {formatVersionTime(data.builtAt)}
            </dd>
            <dt className="text-muted-foreground">Git SHA</dt>
            <dd
              className="font-mono"
              data-testid="version-info-dialog-git-sha"
            >
              {data.gitShaShort ?? data.gitSha ?? '未知'}
            </dd>
            <dt className="text-muted-foreground">元数据来源</dt>
            <dd data-testid="version-info-dialog-source">
              <span
                className={
                  data.source === 'manifest'
                    ? 'inline-flex items-center rounded border border-emerald-400 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700'
                    : 'inline-flex items-center rounded border border-amber-400 bg-amber-50 px-2 py-0.5 text-xs text-amber-700'
                }
              >
                {SOURCE_LABELS[data.source]}
              </span>
            </dd>
            {data.gitRef ? (
              <>
                <dt className="text-muted-foreground">Git Ref</dt>
                <dd
                  className="font-mono break-all"
                  data-testid="version-info-dialog-git-ref"
                >
                  {data.gitRef}
                </dd>
              </>
            ) : null}
            <dt className="text-muted-foreground">查询时间</dt>
            <dd data-testid="version-info-dialog-fetched-at">
              {formatVersionTime(data.fetchedAt)}
            </dd>
          </dl>
        ) : null}

        <div className="mt-5 flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            data-testid="version-info-dialog-close"
          >
            关闭
          </Button>
        </div>
      </div>
    </div>
  );
}
